import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

function detectFormat(filename) {
  return filename.toLowerCase().endsWith('.xml') ? 'camt' : 'csv';
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(file);
  });
}

export function BankImportModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState('csv');
  const [error, setError] = useState(null);
  const [imported, setImported] = useState(null);

  function reset() {
    setFile(null);
    setFormat('csv');
    setError(null);
    setImported(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function onFileChange(e) {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setImported(null);
    setError(null);
    if (selected) {
      setFormat(detectFormat(selected.name));
    }
  }

  const importStatement = useMutation({
    mutationFn: async () => {
      const content = await readFileAsText(file);
      return financeApi.importBankStatement({ format, content });
    },
    onSuccess: (data) => {
      setImported(data?.imported ?? 0);
      queryClient.invalidateQueries({ queryKey: ['finance', 'bank'] });
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError(t('bank.import.fileRequired'));
      return;
    }
    importStatement.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('bank.import.title')}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={importStatement.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} disabled={importStatement.isPending || !file}>
            {importStatement.isPending ? `${t('bank.import.action')}…` : t('bank.import.action')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('bank.import.fileLabel')}</span>
          <input className="input" type="file" accept=".xml,.csv,.txt" onChange={onFileChange} />
        </label>
        <label className="field">
          <span className="field-label">{t('bank.import.formatLabel')}</span>
          <select className="input" value={format} onChange={(e) => setFormat(e.target.value)}>
            <option value="camt">{t('bank.import.formatCamt')}</option>
            <option value="csv">{t('bank.import.formatCsv')}</option>
          </select>
        </label>
        {imported !== null && (
          <div className="alert success">{t('bank.import.success').replace('{count}', String(imported))}</div>
        )}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
